import { useMemo } from "react";
import type { Client, Entry } from "@ascente/shared-types";
import { calcClientTotals, type TotalsSummary } from "../utils/billing.js";
import { parseDate, getDateRangePresets } from "../utils/dateHelpers.js";

export type AnalyticsRange = keyof ReturnType<typeof getDateRangePresets> | "custom";

export interface AnalyticsTotals extends TotalsSummary {
  entryCount: number;
  startDate: string;
  endDate: string;
}

/**
 * Pure derivation of analytics totals from a range selection.
 *
 * `range`/`customStart`/`customEnd` live in AppContext (so they survive
 * tab switches) and are passed in here, mirroring how useDashboardTotals
 * takes rangeStart/rangeEnd as arguments rather than owning state itself.
 */
export function useAnalyticsTotals(
  clients: Client[],
  entries: Entry[],
  range: string,
  customStart: string,
  customEnd: string,
): AnalyticsTotals | null {
  return useMemo<AnalyticsTotals | null>(() => {
    const presets = getDateRangePresets();
    let startStr: string;
    let endStr: string;

    if (range === "custom") {
      if (!customStart || !customEnd) return null;
      startStr = customStart;
      endStr = customEnd;
    } else {
      const preset = presets[range];
      if (!preset) return null;
      startStr = preset.start;
      endStr = preset.end;
    }

    const start = parseDate(startStr);
    const end = parseDate(endStr);
    const activeClients = clients.filter((c) => c.status === "active");

    const byClient: TotalsSummary["byClient"] = {};
    for (const client of activeClients) {
      byClient[client.id] = calcClientTotals(client, entries, start, end);
    }

    const clientTotals = Object.values(byClient);
    const grandSubtotal = clientTotals.reduce((s, t) => s + t.subtotal, 0);
    const grandGST = clientTotals.reduce((s, t) => s + t.gst, 0);
    const grandTotal = clientTotals.reduce((s, t) => s + t.total, 0);
    const totalHours = clientTotals.reduce((s, t) => s + t.hours, 0);
    const totalHourlyCharges = clientTotals.reduce((s, t) => s + t.hourlyTotal, 0);
    const totalTaskCharges = clientTotals.reduce((s, t) => s + t.taskCharges, 0);
    const totalFixedCharges = clientTotals.reduce((s, t) => s + t.fixedTotal, 0);
    const entryCount = clientTotals.reduce((s, t) => s + t.entries.length, 0);

    return {
      byClient,
      grandSubtotal,
      grandGST,
      grandTotal,
      totalHours,
      totalHourlyCharges,
      totalTaskCharges,
      totalFixedCharges,
      entryCount,
      startDate: startStr,
      endDate: endStr,
    };
  }, [clients, entries, range, customStart, customEnd]);
}
